import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { SiteNav } from "./SiteNav";
import { authEnabled } from "../AuthGate";
import { setAuthTokenGetter } from "../../sim/save";
import { adminWhoami, type AdminWhoami } from "../../sim/admin";

/**
 * THE ADMIN SHELL — the frame every admin page sits in: one access check, one sidebar of sections, one status
 * strip, and one page header. The admin console is how players get fixed by hand, so every page is meant to be
 * readable at a glance: where am I (section › page), what is this page for (one line, from NAV), and what is
 * switched on right now (the status strip).
 *
 * NAV IS THE ONE SOURCE of every page's name, icon, section and one-line purpose. A page passes only its
 * `section`; the header, the sidebar and the Dashboard's quick links all read the same entry, so they can never
 * disagree. (A page may still override `title` — the player page shows the player's name.)
 *
 * THE STATUS STRIP (a bar across the top of every page, above the title — it used to sit in the sidebar, which
 * pushed the System tools below the fold on a laptop): which server you are on (LIVE or TEST), and whether
 * maintenance or Kickstarter Mode is on, are on screen on every admin page — the three facts an admin must never be wrong about before changing
 * something.
 *
 * THE ACCESS CHECK runs once per page load and is shared by every tab (`whoamiOnce`), so moving between tabs
 * never flashes "Checking access…". Pages call `useAdmin().refresh()` after changing anything the strip shows.
 * The SERVER is the real gate on every admin route (and writes the audit log); this only decides what to draw.
 */

export type AdminSection =
  | "overview" | "players" | "stats" | "audit" | "settings" | "broadcast" | "kickstarter" | "tuning"
  | "products" | "codes" | "pages" | "theme" | "collisions" | "whats-new" | "prelaunch" | "releases" | "backups"
  | "analytics" | "analytics-live" | "analytics-visitors" | "analytics-revenue" | "analytics-economy"
  | "analytics-engagement" | "analytics-health";

export interface AdminNavItem { id: AdminSection; to: string; icon: string; label: string; blurb: string }

export const ADMIN_NAV: { group: string; items: AdminNavItem[] }[] = [
  { group: "", items: [
    { id: "overview", to: "/admin", icon: "🏠", label: "Dashboard", blurb: "Key numbers, what's switched on, and a shortcut to every admin tool." },
  ] },
  { group: "Players", items: [
    { id: "players", to: "/admin/players", icon: "👥", label: "Players", blurb: "Find a player by handle, name, email or id, and open their account: levels, purse, plants, bag and badges at a glance; fix it (every change previewed, logged and undoable where safe); grant Kickstarter rewards one by one; spectate live or walk their garden." },
    { id: "backups", to: "/admin/backups", icon: "🛟", label: "Backups", blurb: "Copies of every player's save — made every day and before every update, or whenever you want one. Restore a player to any of them in two clicks (what changes is shown first, and it can be undone), download one, or try a player's save on the test server." },
    { id: "stats", to: "/admin/stats", icon: "📊", label: "Usage stats", blurb: "Where active play-time goes across every player, and who plays most." },
  ] },
  // ANALYTICS (site/AdminAnalytics.tsx, docs/ANALYTICS.md): every page shares one date-range picker and reads the
  // rollup tables through /api/admin/analytics/*. Usage stats and the Tutorial funnel stay under Players.
  { group: "Analytics", items: [
    { id: "analytics", to: "/admin/analytics", icon: "📈", label: "Overview", blurb: "The morning glance: visitors, players, money, Petals and errors for the chosen days, each against the period before." },
    { id: "analytics-live", to: "/admin/analytics/live", icon: "🟢", label: "Live", blurb: "Who is on the site and in the game right now, and what they are doing." },
    { id: "analytics-visitors", to: "/admin/analytics/visitors", icon: "🧭", label: "Visitors", blurb: "Every visit in the range — where it came from, on what, and what it did — with each visitor's timeline." },
    { id: "analytics-revenue", to: "/admin/analytics/revenue", icon: "💵", label: "Revenue", blurb: "Money in and out by day and product, per currency: Shop sales, Garden Club payments, refunds and disputes; Club members, churn and MRR; the payer funnel." },
    { id: "analytics-economy", to: "/admin/analytics/economy", icon: "🌸", label: "Economy", blurb: "Petals earned by source and spent by product, coin → Petal exchanges, the Petal liability and coin holdings, the shop / club / exchange funnels, and accounts that stand out." },
    { id: "analytics-engagement", to: "/admin/analytics/engagement", icon: "🎣", label: "Engagement", blurb: "Is the game sticky? DAU / WAU / MAU and stickiness, new vs returning, session length, time per scene, features per player, levels and the demo cap, cohort retention, and the tutorial." },
    { id: "analytics-health", to: "/admin/analytics/health", icon: "🩺", label: "Tech health", blurb: "Is it working and fast? Server errors and latency, client errors, and load and frame times on players' devices." },
  ] },
  { group: "Rewards & Codes", items: [
    { id: "codes", to: "/admin/codes", icon: "🎟️", label: "Codes & grants", blurb: "Make, import and manage redeem codes — pin codes from a CSV, backer codes that bundle Kickstarter rewards and premium plants — see who redeemed them, or grant a bundle straight to one player." },
  ] },
  { group: "Store", items: [
    { id: "products", to: "/admin/products", icon: "🛒", label: "Products", blurb: "What the Shop sells — prices, product cards, and what each purchase grants." },
  ] },
  { group: "Game Tuning", items: [
    { id: "releases", to: "/admin/releases", icon: "🗓️", label: "Releases", blurb: "Turn content updates and content packages on for players — now, on a date and time you choose, or as a “coming soon” preview — and back off again." },
    { id: "kickstarter", to: "/admin/kickstarter", icon: "🚀", label: "Kickstarter mode", blurb: "Cap every skill track for the campaign demo, and write the notice players see." },
    { id: "tuning", to: "/admin/tuning", icon: "🎚️", label: "Tuning editors", blurb: "The live balance editors — decor prices, fish & insect odds, achievements — and where to open each." },
    { id: "collisions", to: "/admin/collisions", icon: "🧱", label: "Collisions", blurb: "Where players can walk around each decor item. Saves go live at once." },
  ] },
  { group: "Site", items: [
    { id: "whats-new", to: "/admin/whats-new", icon: "📰", label: "What's New", blurb: "The patch notes players read on the What's New page — posted automatically when a change marked for a note ships, or written here. Edit, hide or delete any of them; every note is held to the house style." },
    { id: "pages", to: "/admin/pages", icon: "📝", label: "Page builder", blurb: "The sections on each website page, and the home page's rotating banner." },
    { id: "theme", to: "/admin/theme", icon: "🎨", label: "Site theme", blurb: "The website's colours and background pattern — preview here, then publish." },
  ] },
  { group: "System", items: [
    { id: "prelaunch", to: "/admin/prelaunch", icon: "🔒", label: "Pre-launch", blurb: "Turn the whole website into one landing page that points at the Kickstarter — for after the announcement and before the campaign goes live. Admins still see everything." },
    { id: "settings", to: "/admin/settings", icon: "🚧", label: "Maintenance", blurb: "Lock this server for maintenance, choose who can still play, and check which server you're on." },
    { id: "broadcast", to: "/admin/broadcast", icon: "📣", label: "Broadcast", blurb: "Send a message to everyone playing right now, or force their games onto the latest build." },
    { id: "audit", to: "/admin/audit", icon: "📋", label: "Audit log", blurb: "Every change an admin has made, newest first — filter by kind, admin or player." },
  ] },
];

/** The nav entry (and its group) for a section. */
export function adminNavItem(section: AdminSection): { group: string; item: AdminNavItem } {
  for (const g of ADMIN_NAV) for (const item of g.items) if (item.id === section) return { group: g.group, item };
  throw new Error(`unknown admin section ${section}`);
}

/** Where a group's breadcrumb goes: its first tool. */
function groupHome(group: string): string {
  return ADMIN_NAV.find((g) => g.group === group)?.items[0]?.to ?? "/admin";
}

type Gate = "checking" | "denied" | "no-auth" | "ok";

let whoamiOnce: Promise<AdminWhoami> | null = null;

/** DEV-ONLY preview of the console with no backend: `?admindemo` (or the older `?pbdemo` / `?prodemo`) once,
 *  and it holds for the browser session so moving between tabs keeps it. Never true in a production build. */
function devDemo(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    if (/[?&](admindemo|pbdemo|prodemo)(=|&|$)/.test(location.search)) sessionStorage.setItem("shelf-garden.adminDemo", "1");
    return sessionStorage.getItem("shelf-garden.adminDemo") === "1";
  } catch { return false; }
}

const AdminCtx = createContext<{ info: AdminWhoami; refresh: () => void } | null>(null);

/** The server facts for the page you are on (env, maintenance, Kickstarter) + a way to re-read them. */
export function useAdmin(): { info: AdminWhoami; refresh: () => void } {
  const v = useContext(AdminCtx);
  if (!v) throw new Error("useAdmin outside AdminShell");
  return v;
}

/**
 * Wires Clerk's token getter into the save store so the spectate WebSocket can send a verifiable JWT as its
 * first message (a WS upgrade can't send the session cookie the REST routes rely on). Only on an auth build —
 * `useAuth` needs a ClerkProvider.
 */
function AdminAuthBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setAuthTokenGetter(() => getToken());
    return () => setAuthTokenGetter(null);
  }, [getToken]);
  return null;
}

export function AdminShell({ section, title, subtitle, actions, children }: {
  section: AdminSection; title?: string; subtitle?: string; actions?: ReactNode; children: ReactNode;
}) {
  const [info, setInfo] = useState<AdminWhoami | null>(null);
  const [gate, setGate] = useState<Gate>("checking");
  const { group, item } = adminNavItem(section);

  const load = useCallback((force: boolean) => {
    // a build without Clerk has no admins — except in DEV, where the server's own answer decides (so the
    // console can be previewed against a mocked /api/admin/me; a real key-less server never says admin)
    if (!authEnabled && !import.meta.env.DEV) { setGate("no-auth"); return; }
    if (force || !whoamiOnce) {
      whoamiOnce = adminWhoami().then((r) => (devDemo() && !r.admin ? { ...r, admin: true, env: "staging" as const } : r));
    }
    void whoamiOnce.then((r) => {
      setInfo(r);
      setGate(r.admin ? "ok" : r.authEnabled ? "denied" : "no-auth");
    });
  }, []);
  useEffect(() => { load(false); }, [load]);
  const refresh = useCallback(() => load(true), [load]);

  return (
    <div className="site admin-site">
      {authEnabled && <AdminAuthBridge />}
      <SiteNav />
      <div className="adm">
        <aside className="adm-side" aria-label="Admin sections">
          <Link to="/admin" className="adm-brand">🛡️ <span>Admin</span></Link>
          <nav className="adm-nav">
            {ADMIN_NAV.map((g) => (
              <div key={g.group || "home"} className="adm-nav-group">
                {g.group && <div className="adm-nav-label">{g.group}</div>}
                {g.items.map((it) => (
                  <Link key={it.id} to={it.to} className={`adm-nav-item${it.id === section ? " on" : ""}`}
                    aria-current={it.id === section ? "page" : undefined} title={it.blurb}>
                    <span className="adm-nav-ic" aria-hidden="true">{it.icon}</span>{it.label}
                  </Link>
                ))}
              </div>
            ))}
          </nav>
        </aside>
        <main className="adm-main">
          {info && gate === "ok" && <StatusStrip info={info} />}
          <header className="adm-head">
            <div>
              {group && (
                <nav className="adm-crumb" aria-label="Breadcrumb">
                  <Link to="/admin">Admin</Link> › <Link to={groupHome(group)}>{group}</Link>
                  {title && item.label !== group && <> › <Link to={item.to}>{item.label}</Link></>}
                </nav>
              )}
              <h1 className="adm-title"><span className="adm-title-ic" aria-hidden="true">{item.icon}</span>{title ?? item.label}</h1>
              <p className="adm-sub">{subtitle ?? item.blurb}</p>
            </div>
            {gate === "ok" && actions && <div className="adm-head-actions">{actions}</div>}
          </header>
          {gate === "checking" && <Loading what="Checking access" />}
          {gate === "no-auth" && <Card><EmptyState icon="🔒">Admin tools need the signed-in (Clerk) build — they aren't available in this environment.</EmptyState></Card>}
          {gate === "denied" && (
            <Card><EmptyState icon="🔒" action={<Link className="site-btn ghost sm" to="/">← Back to the site</Link>}>You don't have admin access.</EmptyState></Card>
          )}
          {gate === "ok" && info && <AdminCtx.Provider value={{ info, refresh }}>{children}</AdminCtx.Provider>}
        </main>
      </div>
    </div>
  );
}

/** LIVE/TEST + the two switches that change what players get. Always visible, always first. */
function StatusStrip({ info }: { info: AdminWhoami }) {
  const staging = info.env === "staging";
  return (
    <div className="adm-status">
      <span className={`adm-pill ${staging ? "test" : "live"}`}>{staging ? "🧪 Test server" : "🌐 Live server"}</span>
      {info.maintenance && <Link to="/admin/settings" className="adm-pill warn">🚧 Maintenance on</Link>}
      {info.prelaunch.on && <Link to="/admin/prelaunch" className="adm-pill warn">🔒 Pre-launch on — site hidden</Link>}
      {info.kickstarter.on && <Link to="/admin/kickstarter" className="adm-pill ks">🚀 Kickstarter mode · Lv {info.kickstarter.levelCap}</Link>}
      {staging ? (info.liveUrl && <a className="adm-peer" href={`${info.liveUrl}/admin`}>Open live admin ↗</a>)
        : (info.stagingUrl && <a className="adm-peer" href={`${info.stagingUrl}/admin`}>Open test admin ↗</a>)}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  shared building blocks for admin pages
// ---------------------------------------------------------------------------

/** A titled panel. `tone` tints it for things that change what players experience. */
export function Card({ title, icon, actions, tone, children, className }: {
  title?: string; icon?: string; actions?: ReactNode; tone?: "warn" | "ks" | "danger" | "ok"; children: ReactNode; className?: string;
}) {
  return (
    <section className={`adm-card${tone ? ` ${tone}` : ""}${className ? ` ${className}` : ""}`}>
      {(title || actions) && (
        <div className="adm-card-head">
          {title && <h2 className="adm-card-title">{icon && <span aria-hidden="true">{icon} </span>}{title}</h2>}
          {actions && <div className="adm-card-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** One big number with a label (and an optional line under it). */
export function Tile({ label, value, sub, to, tone }: { label: string; value: ReactNode; sub?: ReactNode; to?: string; tone?: "ok" | "warn" | "ks" }) {
  const body = (
    <>
      <div className="adm-tile-v">{value}</div>
      <div className="adm-tile-k">{label}</div>
      {sub && <div className="adm-tile-sub">{sub}</div>}
    </>
  );
  const cls = `adm-tile${tone ? ` ${tone}` : ""}`;
  return to ? <Link to={to} className={`${cls} link`}>{body}</Link> : <div className={cls}>{body}</div>;
}

/** An inline result line (✓ or ⚠). */
export function Notice({ ok, children }: { ok?: boolean; children: ReactNode }) {
  return <div className={ok ? "admin-ok" : "admin-error"} role={ok ? "status" : "alert"}>{ok ? "✓ " : "⚠ "}{children}</div>;
}

/** A load failure, with a Retry when the page can re-fetch. The one way every admin page shows an error. */
export function ErrorNote({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="admin-error adm-error" role="alert">
      <span>⚠ {error}</span>
      {onRetry && <button className="site-btn ghost sm" onClick={onRetry}>Retry</button>}
    </div>
  );
}

/** The one loading line. */
export function Loading({ what = "Loading" }: { what?: string }) {
  return <p className="adm-muted adm-loading" role="status">{what}…</p>;
}

/** "Nothing here yet" — an icon, a sentence that says what WOULD be here, and optionally the button that adds it. */
export function EmptyState({ icon = "🌱", children, action }: { icon?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="adm-empty">
      <span className="adm-empty-ic" aria-hidden="true">{icon}</span>
      <div className="adm-empty-text">{children}</div>
      {action && <div className="adm-empty-action">{action}</div>}
    </div>
  );
}

/** The empty-state row of a table, spanning every column. */
export function EmptyRow({ cols, children }: { cols: number; children: ReactNode }) {
  return <tr><td colSpan={cols} className="admin-empty adm-empty-row">{children}</td></tr>;
}

/**
 * THE ONE CONFIRMATION — asked ONLY before an action that destroys or overwrites something, locks players
 * out, or interrupts them. Reversible changes (switching a code off, turning a mode off, saving a draft) do
 * not ask. `action` is the question; `consequence` says exactly what will happen, and whether it can be undone.
 */
export function confirmDanger(action: string, consequence?: string): boolean {
  return window.confirm(consequence ? `${action}\n\n${consequence}` : action);
}

/** "3m ago" / "2d ago" from epoch ms. */
export function ago(ms: number): string {
  if (!ms) return "never";
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

/** Trim a long "clerk:user_xxx" id for display. */
export function shortId(id: string): string {
  return id.length > 22 ? id.slice(0, 12) + "…" + id.slice(-6) : id;
}

/** Audit action ids → words an admin reads at a glance. */
export function auditLabel(action: string): string {
  const map: Record<string, string> = {
    maintenance: "Maintenance mode", maintenanceAllowlist: "Maintenance allowlist", kickstarter: "Kickstarter mode",
    announce: "Announcement", forceReload: "Force-refresh", editSave: "Edited save", deleteSave: "Deleted save",
    tuning: "Tuning change", decorBands: "Collision bands", syncSaveFromLive: "Synced save from live",
    promoteThemeToLive: "Promoted theme to live", "seed-profiles": "Demo profiles",
    "action:grantClub": "Comped Garden Club", "action:revokeClub": "Removed Garden Club comp",
    "action:grantCoins": "Granted coins", "action:setCoins": "Set coins", "action:grantXp": "Granted XP", "action:setXp": "Set XP",
    "action:giveSeed": "Gave seeds", "action:giveStock": "Gave stock", "action:addRacks": "Added rack",
    "redeem:create": "Made a code", "redeem:update": "Edited a code", "redeem:deactivate": "Switched off a code",
    prelaunch: "Pre-launch mode",
    "redeem:batch": "Generated codes", "redeem:grant": "Granted prizes", "redeem:import": "Imported codes from a CSV",
    "whatsNew:post": "Posted a patch note", "whatsNew:edit": "Edited a patch note", "whatsNew:delete": "Deleted a patch note",
    "redeem:bulk-activate": "Switched on codes", "redeem:bulk-deactivate": "Switched off codes", "redeem:bulk-delete": "Deleted unused codes",
    "action:resetGrowth": "Reset growth", "action:clearSeeds": "Wiped seeds", "action:clearStock": "Wiped stock",
    "account:addCoins": "Gave coins", "account:removeCoins": "Took coins", "account:addPetals": "Gave Petals", "account:removePetals": "Took Petals",
    "account:addSeeds": "Gave seeds", "account:removeSeeds": "Took seeds", "account:addDecor": "Gave decor", "account:removeDecor": "Took decor",
    "account:grantAchievement": "Granted an achievement", "account:revokeAchievement": "Revoked an achievement",
    "account:grantPlant": "Granted a Shop plant", "account:revokePlant": "Revoked a Shop plant", "account:setLevel": "Set a level",
    "account:grantClub": "Comped Garden Club", "account:revokeClub": "Removed Garden Club comp", "account:undo": "Undid a change",
    "account:grantPack": "Granted Kickstarter rewards", "account:revokePack": "Took back Kickstarter rewards",
    "account:revokeCosmetic": "Took back a backer skin",
    "profile:hide": "Hid the public profile", "profile:unhide": "Unhid the public profile",
    "access:create": "Made early-access codes", "access:revoke": "Revoked an early-access code",
    "backups:restore": "Restored a save from a backup", "backups:copy": "Made a copy of a save", "backups:everyone": "Backed up everyone",
    "backups:delete": "Deleted a backup", "backups:edit": "Changed a backup", "backups:import": "Copied a backup from live",
    "backups:loadIntoMine": "Loaded a backup into own test account",
  };
  const words = action.replace(/^action:/, "").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return map[action] ?? words.charAt(0).toUpperCase() + words.slice(1);
}
